import { extendTheme } from "@chakra-ui/react";
import { ThemeConfig, theme as baseTheme } from "@chakra-ui/theme";
import { Button } from "./button/button";
import { Table } from "./table/table";
import { Input } from "./input/input";

// Import fonts
import "@fontsource/poppins/300.css";
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/600.css";
import "@fontsource/fredoka-one/400.css";

const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

export const theme = extendTheme({
  config,
  styles: {
    global: (props: any) => ({
      "html, body": {
        margin: 0,
        padding: 0,
        color: "gray.600",
        backgroundColor: "cherryUi.800",
        overflowX: "hidden",
      },
      "h1, h2, h3, h4, h5, h6": {
        color: props.colorMode === "light" ? "gray.600" : "white",
      },
    }),
  },
  colors: {
    black: "#000",
    white: "#fff",
    cherryGrey: {
      40: "#FAFAFA",
      150: "#EEEEEE",
      160: "#EAEAEA",
      300: "#DDDDDD",
      600: "#AAAAAA",
      700: "#999999",
      900: "#666666",
      1000: "#555555",
    },
    cherryGreyAlpha: {
      40: "#FAFAFA64",
      150: "#EEEEEE64",
      160: "#EAEAEA64",
      300: "#DDDDDD64",
      600: "#AAAAAA64",
      700: "#99999964",
      900: "#66666664",
      1000: "#55555564",
    },
    // Color scheme for cherryhub buttons
    cherryButton: {
      50: "#ED8C83",
      100: "#e04d4c",
      500: "#ee3443", // bg
      600: "#bb2834", // hoverBg
      700: "#6e171e", // activeBg
    },
    cherryButtonDark: {
      50: "#ED8C83",
      100: "#e04d4c",
      500: "#9e1029", // bg
      600: "#6b0a1b", // hoverBg
      700: "#510815", // activeBg
    },
    cherryUi: {
      600: "#c41836",
      700: "#9e1029",
      800: "#7a121e",
    },
  },
  fonts: {
    heading: "Poppins, Helvetica, sans-serif",
    body: "Poppins, Helvetica, sans-serif",
    jsonTextArea: "SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },
  components: {
    List: {
      baseStyle: {
        item: {
          fontSize: "xs",
        },
      },
      variants: {
        empty: {
          item: {
            marginInlineStart: "0 !important",
          },
        },
      },
    },
    Button,
    Table,
    Input,
    IconButton: {
      defaultProps: {
        size: "xs",
      },
    },
    Checkbox: {
      defaultProps: {
        size: "sm",
        colorScheme: "cherryButton",
      },
    },
    Radio: {
      defaultProps: {
        size: "sm",
        colorScheme: "cherryButton",
      },
    },
    Header: {
      baseStyle: {
        fontWeight: "600",
      },
      variants: {
        content: {
          color: "blue",
        },
      },
    },
    Modal: {
      baseStyle: {
        header: {
          backgroundColor: "cherryUi.600",
          color: "#fff",
          borderTopRightRadius: "var(--chakra-radii-md)",
          borderTopLeftRadius: "var(--chakra-radii-md)",
        },
        body: {
          paddingTop: "var(--chakra-space-6)",
        },
        closeButton: {
          color: "#fff",
        },
      },
    },
    Divider: {
      variants: {
        "horizontal-divider": {
          backgroundColor: "#BABABA",
          width: "1.5px",
        },
        section: {
          borderBottomWidth: "1px",
          borderStyle: "solid",
          borderColor: "cherryGrey.150",
          opacity: "1",
          marginBottom: "0.5rem",
        },
        "section-box": {
          borderBottomWidth: "1px",
          borderStyle: "solid",
          borderColor: "cherryGrey.150",
          opacity: "1",
          marginTop: "2",
        },
      },
    },
    Text: {
      baseStyle: {
        fontSize: "xs",
      },
    },
    Breadcrumb: {
      variants: {
        pageHeading: {
          container: {
            marginBottom: "2",
          },
          item: {
            color: "cherryUi.600",
            fontSize: "sm",
          },
          separator: {
            marginInlineStart: "0.25rem",
            marginInlineEnd: "0.25rem",
          },
        },
      },
    },
    Tag: {
      variants: {
        cherryButton: {
          container: {
            height: "2rem",
            paddingInlineStart: "3",
            paddingInlineEnd: "3",
            borderRadius: "full",
            color: "gray.800",
            background: "gray.100",
          },
          label: {
            fontSize: "sm",
          },
        },
      },
    },
    Accordion: {
      variants: {
        section: {
          container: {
            border: "1px solid var(--chakra-colors-cherryGrey-150)",
          },
          panel: {
            paddingTop: "1rem",
            paddingBottom: "1.75rem",
          },
          button: {
            color: "white",
            background: "var(--chakra-colors-cherryGrey-700)",
            _expanded: {
              background: "var(--chakra-colors-cherryUi-600)",
            },
            _hover: {
              background: "var(--chakra-colors-cherryGrey-600)",
            },
            fontSize: "sm",
          },
          icon: {
            color: "var(--chakra-colors-cherryGrey-1000)",
          },
        },

        "detail-section": {
          container: {
            border: "1px solid var(--chakra-colors-cherryGrey-150)",
          },
          panel: {
            paddingTop: "1rem",
            paddingBottom: "0.75rem",
          },
          button: {
            color: "cherryGrey.900",
            background: "cherryGrey.160",
            fontSize: "sm",
            _hover: {
              background: "cherryGrey.150",
            },
          },
          icon: {
            color: "cherryGrey.900",
          },
        },

        "custom-field": {
          container: {
            border: "1px solid var(--chakra-colors-cherryGrey-300)",
            bgColor: "cherryGrey.40",
            borderRadius: "1rem",
          },
          panel: {
            padding: "1.5rem",
          },
          button: {
            borderRadius: "1rem",
            padding: "1rem 1.5rem",
            color: "cherryGrey.900",
            _hover: {
              background: "cherryGrey.40",
            },
            fontSize: "sm",
          },
          icon: {
            color: "cherryGrey.900",
          },
        },
      },
    },
    Tabs: {
      variants: {
        edit: {
          tab: {
            _focus: { boxShadow: "none" },
            _selected: { color: "cherryGrey.900", bg: "white" },
            borderTopLeftRadius: "md",
            borderTopRightRadius: "md",
            bgColor: "cherryGrey.160",
            color: "cherryGrey.1600",
            px: 12,
            boxShadow: "xl",
            fontSize: "xs",
            fontWeight: "bold",
            whiteSpace: "nowrap",
          },
          tablist: {
            px: 4,
            borderBottom: "none",
          },
          tabpanels: {
            boxShadow: "xl",
            borderRadius: "lg",
            px: 4,
            pt: 8,
            pb: 10,
            bgColor: "white",
            position: "relative",
            zIndex: 99,
          },
        },
      },
    },
    Form: {
      baseStyle: {
        container: {
          input: {
            fontSize: "xs",
            height: "8",
          },
          label: {
            fontSize: "xs",
          },
        },
      },
    },
  },
});
